/**
 * src/game/data/battle-campaign.ts — 戰鬥端「闖關地圖」純資料 + 型別.
 *
 * 緣由:老闆 2026-06-06「感覺沒有闖關的感覺」→ 拍板做 Candy Crush 線性關卡地圖.
 * 研究文件:docs/spec/STAGE_PROGRESSION_RESEARCH.md(§4 MVP 線性主幹).
 *
 * 這個檔案**只**放資料與型別,不含任何 Phaser / DOM / progress 寫入邏輯
 * (解鎖推算 / 過關標記 / 配件解鎖在 src/game/progression/battle-campaign-progress.ts)。
 *
 * 雙軌進度鐵則(CLAUDE.md 最高優先):闖關屬「玩遊戲軌(廣度)」。每關的
 *   `stage` / `bossId` / `hubFactionId` 都是戰鬥端參數(對齊
 *   HorizontalBattleScene.init({ bossId, hubFactionId, stage }))。**絕不**含
 *   任何學習端 reference(chapterId / kpId / subject)。
 *
 * 第 1 章 MVP:5 個普通關 + 1 章節 boss 關,線性。每關對應一場現有戰鬥
 * (沿用 horizontal-battle-scene 波次+boss 流程),只換背景門派 / boss / stage
 * 難度係數,不重寫戰鬥。
 */
import type { FactionId } from '../../shared/types';

/** 一個闖關關卡節點(地圖上的一顆圓點)。 */
export interface BattleStageDef {
  /**
   * 關卡唯一 id。格式 `ch{chapter}-s{index}`(普通關)或 `ch{chapter}-boss`
   * (章節 boss 關)。寫進 `battleCampaign.clearedStages` / `stageStars` 當 key,
   * 故必須穩定、不可隨意改(改了會讓既有存檔的過關紀錄對不上)。
   */
  id: string;
  /** 顯示名(地圖節點下方標籤)。 */
  displayName: string;
  /** 所屬章節(1-based)。MVP 只有第 1 章。 */
  chapter: number;
  /** 章節內順序(1-based)。線性解鎖:過第 n 關 → 解鎖第 n+1 關。 */
  order: number;
  /** 是否為章節 boss 關(地圖頂端、過關解鎖配件)。 */
  isBoss: boolean;
  /**
   * 對應戰鬥的門派(決定背景 / boss 池 / 配色)。傳給
   * HorizontalBattleScene.init 的 `hubFactionId`。
   */
  hubFactionId: FactionId;
  /**
   * 戰鬥難度 stage 編號(傳給 init 的 `stage`,battle scene 以此乘難度係數)。
   * 線性遞增,讓後面的關卡更難一點點,但**不影響玩家戰力**(難度由波次參數定)。
   */
  stage: number;
  /**
   * 指定關底 boss id(可選)。boss 關才填;普通關留 undefined → battle scene
   * 依 hubFactionId 自行挑該門派 boss。對齊 init 的 `bossId`。
   */
  bossId?: string;
  /**
   * 地圖上的相對座標(0..1,x 為水平、y 為垂直),由地圖場景換算成像素並
   * 連成彎路。純佈局用,不影響戰鬥。
   */
  mapPos: { x: number; y: number };
}

/**
 * 過關 boss 關 → 解鎖的配件 id(寫進 `unlockedAccessories`,永久廣度)。
 * 第 1 章 boss → 'drone'(僚機)。配件實際戰力仍只在「當局」生效(鐵則:
 * 解鎖只記「擁有資格」,不灌永久戰力)。
 *
 * key = BattleStageDef.id(boss 關);value = 該關通關後解鎖的配件 id 陣列。
 */
export const STAGE_CLEAR_ACCESSORY_UNLOCKS: Readonly<Record<string, readonly string[]>> = {
  'ch1-boss': ['drone'],
  // R-004 D1:第 2 章 boss 解鎖近戰 'scythe'(唯一另一個既有 gated 配件;
  // 對齊 horizontal-battle-scene 三選一 gating 的 autoWeapons key)。第 3 章 boss
  // 暫無新配件(只有 drone/scythe 兩個既有配件),改以更高 commander XP 獎勵
  // 倍率(rewardMult)回饋(D5),不發明不存在的配件 id 造成死獎勵。
  'ch2-boss': ['scythe'],
};

/**
 * 第 1 章關卡定義(線性)。order 1..5 為普通關,order 6 為章節 boss 關。
 * mapPos 排成一條由左下往右上的彎路(Candy Crush 風),boss 在頂端「目標可見」。
 *
 * hubFactionId 走「星算→符文→機械→自然→疆域」順序帶出 5 種背景/敵人換皮,
 * 讓「下一關真的不一樣」(研究建議 D)。boss 關回到星算門當第 1 章首腦。
 */
export const CHAPTER_1_STAGES: readonly BattleStageDef[] = [
  {
    id: 'ch1-s1',
    displayName: '第 1 關 · 前哨',
    chapter: 1,
    order: 1,
    isBoss: false,
    hubFactionId: 'celestial',
    stage: 1,
    mapPos: { x: 0.16, y: 0.82 },
  },
  {
    id: 'ch1-s2',
    displayName: '第 2 關 · 符文林',
    chapter: 1,
    order: 2,
    isBoss: false,
    hubFactionId: 'runic',
    stage: 2,
    mapPos: { x: 0.38, y: 0.66 },
  },
  {
    id: 'ch1-s3',
    displayName: '第 3 關 · 機械場',
    chapter: 1,
    order: 3,
    isBoss: false,
    hubFactionId: 'techno',
    stage: 3,
    mapPos: { x: 0.6, y: 0.7 },
  },
  {
    id: 'ch1-s4',
    displayName: '第 4 關 · 自然谷',
    chapter: 1,
    order: 4,
    isBoss: false,
    hubFactionId: 'elemental',
    stage: 4,
    mapPos: { x: 0.78, y: 0.5 },
  },
  {
    id: 'ch1-s5',
    displayName: '第 5 關 · 疆域關',
    chapter: 1,
    order: 5,
    isBoss: false,
    hubFactionId: 'dominion',
    stage: 5,
    mapPos: { x: 0.6, y: 0.34 },
  },
  {
    id: 'ch1-boss',
    displayName: '章節魔王 · 數獸',
    chapter: 1,
    order: 6,
    isBoss: true,
    hubFactionId: 'celestial',
    stage: 6,
    bossId: 'shu-mo',
    mapPos: { x: 0.4, y: 0.16 },
  },
];

/**
 * 第 2 章關卡定義(R-004 D1)。沿用第 1 章的線性地圖/星級/解鎖機制,只是難度
 * 配方升一階(chapter-difficulty.ts 的 ch2 recipe:HP/攻擊/移速/密度全升、節奏縮短、
 * tier2 boss)。stage 編號**連續遞增**(7..12),讓 chapterForStage() 能把 stage
 * 正確映回 chapter(每 6 關一章)。order 在章內仍 1..6。
 *
 * boss 關用 tier2 boss 'shu-fu'(數符雙煞),對齊 ch2 recipe.bossTier=2(D3)。
 */
export const CHAPTER_2_STAGES: readonly BattleStageDef[] = [
  { id: 'ch2-s1', displayName: '第 1 關 · 鐵衛線', chapter: 2, order: 1, isBoss: false, hubFactionId: 'techno',    stage: 7,  mapPos: { x: 0.16, y: 0.82 } },
  { id: 'ch2-s2', displayName: '第 2 關 · 霜風谷', chapter: 2, order: 2, isBoss: false, hubFactionId: 'elemental', stage: 8,  mapPos: { x: 0.38, y: 0.66 } },
  { id: 'ch2-s3', displayName: '第 3 關 · 封印牆', chapter: 2, order: 3, isBoss: false, hubFactionId: 'runic',     stage: 9,  mapPos: { x: 0.6,  y: 0.7  } },
  { id: 'ch2-s4', displayName: '第 4 關 · 星砲台', chapter: 2, order: 4, isBoss: false, hubFactionId: 'celestial', stage: 10, mapPos: { x: 0.78, y: 0.5  } },
  { id: 'ch2-s5', displayName: '第 5 關 · 疆域塞', chapter: 2, order: 5, isBoss: false, hubFactionId: 'dominion',  stage: 11, mapPos: { x: 0.6,  y: 0.34 } },
  { id: 'ch2-boss', displayName: '章節魔王 · 數符雙煞', chapter: 2, order: 6, isBoss: true, hubFactionId: 'celestial', stage: 12, bossId: 'shu-fu', mapPos: { x: 0.4, y: 0.16 } },
];

/**
 * 第 3 章關卡定義(R-004 D1)。難度配方再升一階(ch3 recipe:最高 HP/攻擊/密度、
 * 最緊湊節奏、tier3 boss)。stage 編號連續(13..18)。
 *
 * boss 關用 tier3 boss 'shi-zhong'(期中總魔・三相之劫),對齊 ch3 recipe.bossTier=3(D3)。
 */
export const CHAPTER_3_STAGES: readonly BattleStageDef[] = [
  { id: 'ch3-s1', displayName: '第 1 關 · 殲滅場', chapter: 3, order: 1, isBoss: false, hubFactionId: 'dominion',  stage: 13, mapPos: { x: 0.16, y: 0.82 } },
  { id: 'ch3-s2', displayName: '第 2 關 · 烈焰廊', chapter: 3, order: 2, isBoss: false, hubFactionId: 'elemental', stage: 14, mapPos: { x: 0.38, y: 0.66 } },
  { id: 'ch3-s3', displayName: '第 3 關 · 沉默殿', chapter: 3, order: 3, isBoss: false, hubFactionId: 'runic',     stage: 15, mapPos: { x: 0.6,  y: 0.7  } },
  { id: 'ch3-s4', displayName: '第 4 關 · 機甲核', chapter: 3, order: 4, isBoss: false, hubFactionId: 'techno',    stage: 16, mapPos: { x: 0.78, y: 0.5  } },
  { id: 'ch3-s5', displayName: '第 5 關 · 星淵口', chapter: 3, order: 5, isBoss: false, hubFactionId: 'celestial', stage: 17, mapPos: { x: 0.6,  y: 0.34 } },
  { id: 'ch3-boss', displayName: '章節魔王 · 三相之劫', chapter: 3, order: 6, isBoss: true, hubFactionId: 'celestial', stage: 18, bossId: 'shi-zhong', mapPos: { x: 0.4, y: 0.16 } },
];

/**
 * 全部章節串成單一線性序列(R-004 D1:開到第 3 章)。地圖場景與解鎖推算都讀這份,
 * 順序即「線性主幹」順序(以 chapter 再 order 排序為權威)。
 */
export const BATTLE_CAMPAIGN_STAGES: readonly BattleStageDef[] = [
  ...CHAPTER_1_STAGES,
  ...CHAPTER_2_STAGES,
  ...CHAPTER_3_STAGES,
]
  .slice()
  .sort((a, b) => (a.chapter - b.chapter) || (a.order - b.order));

/**
 * 全部章節編號(升冪、去重)。R-004 hotfix(boss 2026-06-13「地圖壞了」):
 * 三章共用同一組 mapPos 彎路座標,18 個節點全畫在地圖上會疊成三層(ch2/ch3
 * 的鎖蓋住 ch1 的星、三組關卡名糊在一起)→ 地圖場景改「一次畫一章」分頁,
 * 這兩個 helper 是分頁的純資料來源(可單元測試)。
 */
export function campaignChapters(): number[] {
  return [...new Set(BATTLE_CAMPAIGN_STAGES.map((s) => s.chapter))].sort((a, b) => a - b);
}

/** 某章的關卡(已照 order 升冪)。未知章節回空陣列。 */
export function stagesOfChapter(chapter: number): BattleStageDef[] {
  return BATTLE_CAMPAIGN_STAGES
    .filter((s) => s.chapter === chapter)
    .sort((a, b) => a.order - b.order);
}

/** 依 id 取關卡定義;找不到回 undefined。 */
export function getStageDef(stageId: string): BattleStageDef | undefined {
  return BATTLE_CAMPAIGN_STAGES.find((s) => s.id === stageId);
}

/** 線性序列中,某關卡的索引(0-based);找不到回 -1。 */
export function stageIndex(stageId: string): number {
  return BATTLE_CAMPAIGN_STAGES.findIndex((s) => s.id === stageId);
}
